import { addDoc, collection, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { API_BASE_URL } from '../config';

const POLL_INTERVAL_MS = 12000;
const DISABLED_ACTIONS = new Set(['page viewed']);
const ACTOR_CACHE_TTL_MS = 60 * 1000;

let cachedUsersByUid = new Map();
let cachedUsersAtMs = 0;

const shouldSkipAction = (action) => {
  const normalized = String(action || '').trim().toLowerCase();
  return DISABLED_ACTIONS.has(normalized);
};

export const AUDIT_LOG_STRUCTURE = {
  action: 'string',
  details: 'string',
  severity: 'Info | Warning | Error | Critical | High',
  category: 'authentication | authorization | navigation | user_management | general',
  outcome: 'success | failure | blocked | partial_success',
  actor: {
    uid: 'string | null',
    email: 'string',
    role: 'string',
    name: 'string | null',
    username: 'string | null',
    isAuthenticated: 'boolean',
  },
  actorDisplay: 'string',
  ip: 'string',
  source: {
    app: 'string',
    environment: 'string',
    module: 'string',
    platform: 'web',
  },
  request: {
    path: 'string',
    fullUrl: 'string | null',
    referrer: 'string | null',
    userAgent: 'string | null',
    language: 'string | null',
    timezone: 'string | null',
    screen: 'string | null',
  },
  resource: {
    type: 'string',
    id: 'string | null',
    name: 'string | null',
    path: 'string | null',
  },
  metadata: {
    module: 'string',
    outcome: 'success | failure | blocked | partial_success',
    extra: 'Record<string, unknown>',
  },
  tags: 'string[]',
  sessionId: 'string',
  createdAt: 'Firestore serverTimestamp',
  createdAtMs: 'number',
};

const getOrCreateSessionId = () => {
  const fallback = `session-${Date.now()}`;
  if (typeof window === 'undefined') return fallback;

  try {
    const existing = window.sessionStorage.getItem('avisa_session_id');
    if (existing) return existing;

    const newId = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem('avisa_session_id', newId);
    return newId;
  } catch {
    return fallback;
  }
};

const getWindowContext = () => {
  if (typeof window === 'undefined') {
    return {
      fullUrl: null,
      path: null,
      referrer: null,
      userAgent: 'node-runtime',
      language: 'en-US',
      screen: null,
    };
  }

  return {
    fullUrl: window.location?.href || null,
    path: window.location?.pathname || null,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent || 'unknown' : 'unknown',
    language: typeof navigator !== 'undefined' ? navigator.language || 'en-US' : 'en-US',
    screen: getScreenInfo(),
  };
};

const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const getAuthHeaders = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Not signed in.');
  }

  const idToken = await currentUser.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
};

const getScreenInfo = () => {
  if (typeof window === 'undefined' || !window.screen) return null;
  return `${window.screen.width || 0}x${window.screen.height || 0}`;
};

const normalizeSeverity = (severity) => {
  const value = String(severity || 'Info').trim().toLowerCase();
  if (value === 'critical') return 'Critical';
  if (value === 'error') return 'Error';
  if (value === 'warning' || value === 'warn') return 'Warning';
  if (value === 'high') return 'High';
  return 'Info';
};

const normalizeOutcome = (outcome) => {
  const value = String(outcome || 'success').trim().toLowerCase();
  if (value === 'failure') return 'failure';
  if (value === 'blocked') return 'blocked';
  if (value === 'partial_success' || value === 'partial-success' || value === 'partial success') {
    return 'partial_success';
  }
  return 'success';
};

const normalizeTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return Array.from(
    new Set(
      tags
        .map((tag) => String(tag || '').trim())
        .filter((tag) => tag.length > 0)
    )
  );
};

const createDefaultActor = () => ({
  uid: null,
  email: null,
  role: 'unknown',
  name: null,
  username: null,
  isAuthenticated: false,
});

/**
 * Normalize a user object to the strict flat Firestore schema.
 * - Preserve any provided input values (including null).
 * - Return only the exact keys required by the schema (no extras).
 */
export const normalizeUserForFirestore = (input = {}) => {
  const user = input || {};
  const has = (k) => Object.prototype.hasOwnProperty.call(user, k);

  return {
    barangay: has('barangay') ? user.barangay : '',
    bookmarkCounts: has('bookmarkCounts') ? user.bookmarkCounts : 0,
    createdAt: has('createdAt') ? user.createdAt : null,
    // reset cooldown (ms or timestamp) and reset count stored as integers
    resetCooldown: has('resetCooldown') ? user.resetCooldown : 0,
    resetCount: has('resetCount') ? user.resetCount : 0,
    email: has('email') ? user.email : '',
    isUserOnline: has('isUserOnline') ? user.isUserOnline : false,
    isVerified: has('isVerified') ? user.isVerified : false,
    language: has('language') ? user.language : '',
    lastLogin: has('lastLogin') ? user.lastLogin : 0,
    municipality: has('municipality') ? user.municipality : '',
    name: has('name') ? user.name : '',
    profilePhoto: has('profilePhoto') ? user.profilePhoto : null,
    province: has('province') ? user.province : '',
    region: has('region') ? user.region : '',
    role: has('role') ? user.role : '',
    username: has('username') ? user.username : '',
  };
};

/**
 * Merge existing Firestore user data with incoming update input, then
 * normalize to the strict flat schema. This prevents adding new fields
 * (only the exact schema keys are returned) and preserves real values
 * from `existing` unless the `input` explicitly provides a value.
 */
export const mergeAndNormalizeUserForFirestore = (existing = {}, input = {}) => {
  const existingObj = existing || {};
  const user = input || {};
  const hasInput = (k) => Object.prototype.hasOwnProperty.call(user, k);
  const hasExisting = (k) => Object.prototype.hasOwnProperty.call(existingObj, k);

  return {
    barangay: hasInput('barangay') ? user.barangay : hasExisting('barangay') ? existingObj.barangay : '',
    bookmarkCounts: hasInput('bookmarkCounts') ? user.bookmarkCounts : hasExisting('bookmarkCounts') ? existingObj.bookmarkCounts : 0,
    createdAt: hasInput('createdAt') ? user.createdAt : hasExisting('createdAt') ? existingObj.createdAt : null,
    // reset cooldown (ms or timestamp) and reset count stored as integers
    resetCooldown: hasInput('resetCooldown') ? user.resetCooldown : hasExisting('resetCooldown') ? existingObj.resetCooldown : 0,
    resetCount: hasInput('resetCount') ? user.resetCount : hasExisting('resetCount') ? existingObj.resetCount : 0,
    email: hasInput('email') ? user.email : hasExisting('email') ? existingObj.email : '',
    isUserOnline: hasInput('isUserOnline') ? user.isUserOnline : hasExisting('isUserOnline') ? existingObj.isUserOnline : false,
    isVerified: hasInput('isVerified') ? user.isVerified : hasExisting('isVerified') ? existingObj.isVerified : false,
    language: hasInput('language') ? user.language : hasExisting('language') ? existingObj.language : '',
    lastLogin: hasInput('lastLogin') ? user.lastLogin : hasExisting('lastLogin') ? existingObj.lastLogin : 0,
    municipality: hasInput('municipality') ? user.municipality : hasExisting('municipality') ? existingObj.municipality : '',
    name: hasInput('name') ? user.name : hasExisting('name') ? existingObj.name : '',
    profilePhoto: hasInput('profilePhoto') ? user.profilePhoto : hasExisting('profilePhoto') ? existingObj.profilePhoto : null,
    province: hasInput('province') ? user.province : hasExisting('province') ? existingObj.province : '',
    region: hasInput('region') ? user.region : hasExisting('region') ? existingObj.region : '',
    role: hasInput('role') ? user.role : hasExisting('role') ? existingObj.role : '',
    username: hasInput('username') ? user.username : hasExisting('username') ? existingObj.username : '',
  };
};

const getCachedUserByUid = (uid) => {
  if (!uid) return null;
  if (Date.now() - cachedUsersAtMs > ACTOR_CACHE_TTL_MS) return null;
  return cachedUsersByUid.get(uid) || null;
};

const refreshUsersCache = async () => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/users`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    return;
  }

  const body = await response.json().catch(() => ({}));
  const users = Array.isArray(body?.users) ? body.users : [];
  const nextMap = new Map();

  users.forEach((user) => {
    if (user?.uid) {
      nextMap.set(user.uid, {
        uid: user.uid || null,
        email: user.email || null,
        name: user.name || null,
        username: user.username || null,
        role: user.role || null,
      });
    }
  });

  cachedUsersByUid = nextMap;
  cachedUsersAtMs = Date.now();
};

const resolveActor = async (inputActor = null) => {
  const firebaseUser = auth.currentUser;

  const actor = {
    uid: inputActor?.uid ?? firebaseUser?.uid ?? null,
    email: inputActor?.email ?? firebaseUser?.email ?? null,
    name: inputActor?.name ?? firebaseUser?.displayName ?? null,
    username: inputActor?.username ?? null,
    role: inputActor?.role ?? 'unknown',
    isAuthenticated: inputActor?.isAuthenticated ?? !!firebaseUser,
  };

  if (!actor.uid) {
    return actor;
  }

  if (actor.email && actor.name && actor.username && actor.role && actor.role !== 'unknown') {
    return actor;
  }

  let cached = getCachedUserByUid(actor.uid);
  if (!cached) {
    try {
      await refreshUsersCache();
      cached = getCachedUserByUid(actor.uid);
    } catch {
      // Keep best-effort actor data if enrichment fails.
    }
  }

  if (!cached) {
    return actor;
  }

  return {
    uid: actor.uid,
    email: actor.email ?? cached.email ?? null,
    name: actor.name ?? cached.name ?? null,
    username: actor.username ?? cached.username ?? null,
    role: actor.role && actor.role !== 'unknown' ? actor.role : cached.role ?? 'unknown',
    isAuthenticated: actor.isAuthenticated,
  };
};

const formatDefaultDetails = ({ action, actorDisplay, resource, actorRole }) => {
  const map = {
    add_user: 'added',
    delete_user: 'removed',
    add_admin: 'added',
    delete_admin: 'removed',
    add_document: 'added',
    delete_document: 'deleted',
    admin_login: 'signed in',
    admin_offline: 'went offline',
    promote_admin_to_system_admin: 'promoted',
    demote_system_admin_to_admin: 'demoted',
    change_password: 'changed password for',
    change_username: 'changed username for',
  };

  const role = actorRole === 'sysadmin' ? 'System Admin' :
               actorRole === 'admin' ? 'Admin' :
               actorRole === 'user' ? 'User' : 'System';

  const actionText = String(action || '').toLowerCase();
  const target = resource?.name || resource?.type || 'a resource';

  if (actionText === 'admin_login' || actionText.includes('login')) {
    return `${role} ${actorDisplay} signed in successfully`;
  }
  
  if (actionText.includes('upload') || actionText.includes('scan')) {
    return `${role} ${actorDisplay} uploaded a file ${target}`;
  }

  if (actionText.includes('delete') || actionText.includes('removed')) {
    return `${role} ${actorDisplay} deleted ${target}`;
  }

  if (actionText.includes('update') || actionText.includes('change')) {
    if (actionText.includes('username')) return `${role} ${actorDisplay} updated ${target}'s username`;
    return `${role} ${actorDisplay} updated ${target}'s profile`;
  }

  const verb = map[action] || action.replace(/_/g, ' ');
  return `${role} ${actorDisplay} ${verb} ${target}`;
};

const buildLogPayloadAsync = async ({
  action,
  details,
  severity = 'Info',
  category = 'general',
  outcome = 'success',
  resource = null,
  metadata = {},
  tags = [],
  actor,
  path,
  fullUrl,
  module,
}) => {
  const ctx = getWindowContext();
  const safeActor = (await resolveActor(actor)) || createDefaultActor();
  const safePath = path || ctx.path || 'unknown';
  const finalOutcome = normalizeOutcome(metadata?.outcome || outcome);

  // Prefer a readable actor display: email > username > name > uid
  const actorDisplay = safeActor.email || safeActor.username || safeActor.name || (safeActor.uid ? `uid:${safeActor.uid}` : 'unknown-actor');

  const normalizedResource = {
    type: resource?.type || 'unknown',
    id: resource?.id ?? null,
    name: resource?.name ?? null,
    path: resource?.path ?? null,
  };

  // Prefer explicit `metadata.reason` or `metadata.extra.reason` when available.
  const reason = metadata?.reason ?? (metadata?.extra && metadata.extra.reason) ?? metadata?.why ?? null;

  // Summarize changed fields if the caller passed them in metadata.
  const summarizeChanges = (m) => {
    const changes = m?.extra?.changes || m?.changes || null;
    if (!changes || typeof changes !== 'object') return null;

    try {
      const parts = [];
      for (const [field, val] of Object.entries(changes)) {
        // Support various change shapes: {from, to}, [from,to], or simple new value
        if (val && typeof val === 'object' && ('from' in val || 'to' in val)) {
          parts.push(`${field} from ${String(val.from ?? 'null')} → ${String(val.to ?? 'null')}`);
        } else if (Array.isArray(val) && val.length >= 2) {
          parts.push(`${field} from ${String(val[0])} → ${String(val[1])}`);
        } else {
          parts.push(`${field} → ${String(val)}`);
        }
      }
      return parts.join('; ');
    } catch {
      return null;
    }
  };

  const changesSummary = summarizeChanges(metadata);

  const descriptiveDetails =
    typeof details === 'string' && details.trim().length > 0
      ? details.trim()
      : formatDefaultDetails({
          action,
          category,
          outcome: finalOutcome,
          actorDisplay,
          actorRole: safeActor?.role,
          resource: normalizedResource,
          requestPath: safePath,
          reason,
          changesSummary,
        });

  return {
    action,
    details: descriptiveDetails,
    severity: normalizeSeverity(severity),
    category,
    outcome: finalOutcome,
    actor: safeActor,
    actorDisplay,
    ip: 'client-ip-unavailable',
    module: module || metadata?.module || 'frontend',
    source: {
      app: process.env.REACT_APP_APP_NAME || 'avisa-web',
      environment: process.env.NODE_ENV || 'development',
      module: module || metadata?.module || 'frontend',
      platform: 'web',
    },
    request: {
      path: safePath,
      fullUrl: fullUrl || ctx.fullUrl,
      referrer: ctx.referrer,
      userAgent: ctx.userAgent,
      language: ctx.language,
      timezone: getTimezone(),
      screen: ctx.screen,
    },
    resource: normalizedResource,
    metadata: {
      module: module || metadata?.module || 'frontend',
      outcome: finalOutcome,
      extra: metadata && typeof metadata === 'object' ? metadata : {},
    },
    tags: normalizeTags(tags),
    sessionId: getOrCreateSessionId(),
    createdAtMs: Date.now(),
  };
};

export const writeAuditLog = async ({
  action,
  details,
  severity = 'Info',
  category = 'general',
  outcome = 'success',
  resource = null,
  metadata = {},
  tags = [],
  actor,
  path,
  fullUrl,
  module,
}) => {
  if (!action || typeof action !== 'string') {
    throw new Error('Audit log action is required.');
  }

  if (shouldSkipAction(action)) {
    return null;
  }

  const payload = await buildLogPayloadAsync({
    action,
    details,
    severity,
    category,
    outcome,
    resource,
    metadata,
    tags,
    actor,
    path,
    fullUrl,
    module,
  });

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/audit-logs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => ({}));
    if (!response.ok || body?.ok !== true) {
      throw new Error(body?.error || 'Failed to write audit log.');
    }

    return body.id || null;
  } catch (apiError) {
    // Fallback to direct Firestore write when backend logging fails.
    try {
      const ref = await addDoc(collection(db, 'auditLogs'), {
        ...payload,
        createdAt: serverTimestamp(),
      });
      return ref.id || null;
    } catch {
      throw apiError;
    }
  }
};

// --- Flat-structure logger helpers (writes to Firestore `auditLogs` collection) ---

const flattenObject = (obj, prefix = '') => {
  const out = {};

  const add = (key, value) => {
    if (value === undefined) return;
    out[key] = value === null ? null : value;
  };

  for (const [k, v] of Object.entries(obj || {})) {
    const key = prefix ? `${prefix}_${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
      const nested = flattenObject(v, key);
      Object.assign(out, nested);
    } else {
      add(key, v);
    }
  }

  return out;
};

const writeFlatAuditLog = async (opts) => {
  const payload = await buildLogPayloadAsync(opts);

  // Flatten payload into a single-level object, preserving arrays as-is.
  const flat = flattenObject(payload);

  // Ensure tags remain as array if present
  if (Array.isArray(payload.tags)) {
    flat.tags = payload.tags;
  }

  // Preserve createdAtMs and add server timestamp
  flat.createdAtMs = payload.createdAtMs || Date.now();

  const doc = {
    ...flat,
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'auditLogs'), doc);
  return ref.id || null;
};

// Named convenience functions for common admin/user/document events
export const logAdminLogin = async ({ actor, details, resource, metadata, tags } = {}) => {
  const id = await writeFlatAuditLog({ action: 'admin_login', details, category: 'authentication', outcome: 'success', actor, resource, metadata, tags });

  try {
    const resolved = await resolveActor(actor);
    const uid = resolved?.uid;
    if (uid) {
      // Mark user online in Firestore; best-effort (don't fail the audit logging)
      try {
        const ref = doc(db, 'users', uid);
        await updateDoc(ref, { isUserOnline: true, lastSeenAtMs: Date.now() });
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }

  return id;
};

export const logAdminOffline = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'admin_offline', details, category: 'authentication', outcome: 'success', actor, resource, metadata, tags });

export const logAddUser = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'add_user', details, category: 'user_management', outcome: 'success', actor, resource, metadata, tags });

export const logDeleteUser = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'delete_user', details, category: 'user_management', outcome: 'success', actor, resource, metadata, tags });

export const logAddAdmin = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'add_admin', details, category: 'user_management', outcome: 'success', actor, resource, metadata, tags });

export const logDeleteAdmin = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'delete_admin', details, category: 'user_management', outcome: 'success', actor, resource, metadata, tags });

export const logPromoteAdminToSystemAdmin = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'promote_admin_to_system_admin', details, category: 'authorization', outcome: 'success', actor, resource, metadata, tags });

export const logDemoteSystemAdminToAdmin = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'demote_system_admin_to_admin', details, category: 'authorization', outcome: 'success', actor, resource, metadata, tags });

export const logAddDocument = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'add_document', details, category: 'document_management', outcome: 'success', actor, resource, metadata, tags });

export const logDeleteDocument = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'delete_document', details, category: 'document_management', outcome: 'success', actor, resource, metadata, tags });

export const logChangePassword = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'change_password', details, category: 'authentication', outcome: 'success', actor, resource, metadata, tags });

export const logChangeUsername = async ({ actor, details, resource, metadata, tags } = {}) =>
  writeFlatAuditLog({ action: 'change_username', details, category: 'authentication', outcome: 'success', actor, resource, metadata, tags });


export const writeAuditLogSafe = async (entry) => {
  try {
    return await writeAuditLog(entry);
  } catch (error) {
    console.error('Failed to write audit log:', error);
    return null;
  }
};

const normalizeLogDoc = (data) => {
  return {
    id: data.id || null,
    action: data.action || 'Unknown action',
    details: data.details || '',
    severity: data.severity || 'Info',
    category: data.category || 'general',
    outcome: data.outcome || 'success',
    actorDisplay: data.actorDisplay || (data.actor && (data.actor.email || data.actor.username)) || 'Unknown user',
    actorEmail: data.actor?.email || null,
    actorRole: data.actor?.role || null,
    actorUid: data.actor?.uid || null,
    resourceId: data.resource?.id || null,
    resourceName: data.resource?.name || null,
    resourceType: data.resource?.type || null,
    createdAtIso: data.createdAtIso || null,
    createdAtMs: typeof data.createdAtMs === 'number' ? data.createdAtMs : data.timeMs || 0,
    timeMs: typeof data.timeMs === 'number' ? data.timeMs : data.createdAtMs || 0,
    sourceApp: data.sourceApp || null,
    tags: Array.isArray(data.tags) ? data.tags : [],
  };
};

export const fetchAuditLogs = async ({ max = 200 } = {}) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/audit-logs?max=${encodeURIComponent(max)}`, {
    method: 'GET',
    headers,
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.ok !== true) {
    throw new Error(body?.error || 'Failed to fetch audit logs.');
  }

  const logs = Array.isArray(body.logs) ? body.logs : [];
  return logs.map(normalizeLogDoc);
};

export const subscribeAuditLogs = ({ max = 200, onData, onError }) => {
  let isCancelled = false;

  const load = async () => {
    try {
      const logs = await fetchAuditLogs({ max });
      if (!isCancelled && typeof onData === 'function') {
        onData(logs);
      }
    } catch (error) {
      if (!isCancelled && typeof onError === 'function') {
        onError(error);
      }
    }
  };

  load();
  const timer = setInterval(load, POLL_INTERVAL_MS);

  return () => {
    isCancelled = true;
    clearInterval(timer);
  };
};
